import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Header, Footer } from '../Common';
import SecondaryBanner from '@ajat/design-system/ContentBlocks/SecondaryBanner';
import FomoBlock from '@ajat/design-system/ContentBlocks/FomoBlock';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import Fullwidth from '@ajat/design-system/ContentBlocks/Fullwidth';
import Badge from '@ajat/design-system/Badge';
import Button from '@ajat/design-system/Button';
import { CLUB_PRODUCTS_PRIMARY, CLUB_PRODUCTS_SECONDARY } from './clubProductsData';
import { getStudentClub } from './studentClubData';
import partner1Img from '../../../../assets/cls-fi/landing/about.jpg';
import partner2Img from '../../../../assets/cls-fi/landing/banner-1.jpg';
import '../../Common/SchoolShop/SchoolShop.css';
import '../../Common/StudentClub/ClubShop.css';

/* ══════════════════════════════════════════════════════════════════════════
 * CLS-FI — Student Club destination page
 * Renders after a club is selected & authenticated on the entry form.
 * Follows the CLS SchoolShop visual pattern (photo SecondaryBanner:
 * tag = category, title = club name), with Finnish copy and CLS-specific product mock data.
 * ══════════════════════════════════════════════════════════════════════════ */
const ClubShop = () => {
  const navigate = useNavigate();
  const { clubSlug } = useParams();

  const club = useMemo(() => getStudentClub(clubSlug), [clubSlug]);

  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'CLS');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  const go = (path) => navigate(path);

  const primaryItems = CLUB_PRODUCTS_PRIMARY.map((p) => ({
    ...p,
    onClick: () => go('/clothing/cls-fi/product-details/hoodie'),
  }));

  const secondaryItems = CLUB_PRODUCTS_SECONDARY.map((p) => ({
    ...p,
    onClick: () => go('/clothing/cls-fi/product-details/hoodie'),
  }));

  return (
    <div className="school-shop club-shop">
      <Header />

      <SecondaryBanner
        tag="Opiskelijakerho"
        title={club?.name ?? ''}
      />

      <main className="school-shop__wrapper">
        <div className="school-shop__fomo-container">
          <FomoBlock
            showIcon={false}
            text="Vastaanotamme vain tukkutilauksia. Varmista, että tilaat yli 10 tuotetta."
          />
        </div>

        {/* ── Products Widget 1 ── */}
        <ProductsGrid
          paddingTop="medium-l"
          paddingBottom="medium-s"
          background="var(--color-gray-0)"
          ctaIcon={<ArrowRight size={20} />}
          items={primaryItems}
        />

        {/* ── Fullwidth Content Widget ── */}
        <Fullwidth
          showImage={false}
          subtitle="+ 75 000 ASIAKASTA VUOSITTAIN"
          mainCopyVariant="h2"
          mainCopy="Suomen terävimmät hinnat hintatakuulla, nopea toimitus ja houkuttelevat pakettiratkaisut."
          additionalCopy="Tilaa turvallisesti yli 20 vuoden kokemuksella — ja saa ilmainen paita kahdelle edustajalle."
          tags={['A4 selkäpainatus', '10 × 10 cm rintalogo', 'Ilmainen toimitus', 'Helppo yksilöllinen maksu', 'Vapaa tuotevalinta']}
          showHyperlink={false}
        />

        {/* ── Products Widget 2 ── */}
        <ProductsGrid
          background="var(--color-gray-0)"
          paddingTop="medium-m"
          paddingBottom="large-s"
          items={secondaryItems}
        />

        {/* ── Second SEO block ── */}
        <Fullwidth
          showImage={false}
          subtitle="ETKÖ LÖYTÄNYT ETSIMÄÄSI?"
          mainCopyVariant="h2"
          mainCopy="Tutustu kumppaneihimme ja löydä lisää eksklusiivisia tarjouksia ja tuotteita opiskelijakerhoille."
          showTags={false}
          showHyperlink={false}
        />

        {/* ── Partners block ── */}
        <section className="club-shop__partners">
          <div className="club-shop__partners-grid">
            <article className="club-shop__partner-card">
              <div className="club-shop__partner-visual">
                <img src={partner1Img} alt="Party Kings" />
              </div>
              <div className="club-shop__partner-content">
                <Badge size="l" shape="square" variant="success">10% ALENNUS</Badge>
                <h3 className="club-shop__partner-title typo-h2">Party Kings</h3>
                <Button
                  variant="outline"
                  size="secondary"
                  iconRight={<ArrowRight size={20} />}
                  onClick={() => window.open('#', '_blank', 'noopener,noreferrer')}
                >
                  LUE LISÄÄ
                </Button>
              </div>
            </article>

            <article className="club-shop__partner-card">
              <div className="club-shop__partner-visual">
                <img src={partner2Img} alt="Toinen kumppani" />
              </div>
              <div className="club-shop__partner-content">
                <Badge size="l" shape="square" variant="success">10% ALENNUS</Badge>
                <h3 className="club-shop__partner-title typo-h2">Toinen kumppani</h3>
                <Button
                  variant="outline"
                  size="secondary"
                  iconRight={<ArrowRight size={20} />}
                  onClick={() => window.open('#', '_blank', 'noopener,noreferrer')}
                >
                  LUE LISÄÄ
                </Button>
              </div>
            </article>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ClubShop;
